import { useContext, useEffect, useCallback, useMemo, ReactNode, useRef } from 'react';
import { ReactReduxContext, useDispatch } from 'react-redux';

import { AxiosRequestConfig, AxiosResponse, AxiosRequestTransformer, Method } from 'axios';
import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import { ResourceState } from 'ia-react-core/~/interfaces/ResourceState';
import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import { TakeEffect } from 'redux-saga/effects';
import { MapScopedKeyMap } from 'ia-react-core/~/interfaces/MapScopedKeyMap';
import requestResourceById from '../../../actions/resource-fn/requestResourceById';
import resolveScope from '../../../utils/redux/#resolveScope';
import getDataDst from '../../../utils/components/#getDataDst';
import LoadBufferContext from '../../loader/#LoadBufferContext';

import ResourceGroupContext from '../#ResourceGroupContext';
import getScoped from '../../../utils/redux/getScoped';
import useTranslation from '../../../hooks/useTranslation';
import SchemaContext from '../../schema/#SchemaContext';

export interface ResourceProps<Data, Error = {}> {
  /** Optional function that will receive the `resourceId` and `name`. */
  children?: ({
    name,
    resourceId,
    dataDst,
  }: {
    /** The name of the resource, used as the reducer key. If not provided, the reducer key will be "Resource${resourceId}". */
    name: string;
    /** @ignore */
    resourceId: number;
    /** The scope path where to save the response data to, or a function that returns it. */
    dataDst: string;
  }) => ReactNode;
  /** The url of the request. */
  url?: string;
  /** The name of the resource, used as the reducer key. If not provided, the reducer key will be "Resource${resourceId}". */
  name?: string;
  /** Additional axios config to pass to the request. */
  config?: AxiosRequestConfig;
  /** Object url parameters (query string). Alias for `config.params`. */
  params?: AxiosRequestConfig['params'];
  /** Function to transform the request. Alias for `config.transformRequest`. */
  transformRequest?: AxiosRequestTransformer | AxiosRequestTransformer[];
  /** Function to transform the response. Alias for `config.transformResponse`. */
  transformResponse?: AxiosRequestTransformer | AxiosRequestTransformer[];
  /** Object of header to add to the request. Alias for `config.headers`. */
  headers?: any;
  /** Automatically request the resource once on *componentDidMount*. */
  autoRequest?: boolean;
  /** Automatically request the resource only when is empty. */
  autoRequestIfEmpty?: boolean;
  /** Automatically request the resource when language changes. */
  requestOnLanguageChange?: boolean;
  /** The saga effect to use. Either a function representing a saga effect, or `"latest"` to only accept the latest request, `"every"` to accept every request in parallel and `"sequence"` to accept every request in sequence. The default effect is `"latest"`. */
  effect?: ((...args: unknown[]) => unknown) | 'latest' | 'every' | 'sequence';
  /** Callback called with the axios request config. Must return the final config of the request. */
  onRequest?: (config: AxiosRequestConfig) => void;
  /** Callback called with the response when the request is successful. */
  onSuccess?: (data: AxiosResponse<Data>) => void;
  /** Callback called with the error when the request is not successful. */
  onFailure?: (error: Error) => void;
  /** Callback when the request ends, either in success or failure. */
  onComplete?: () => void;
  /** Callback when the request is cancelled. */
  onCancel?: () => void;
  resourceSrc?: string;
  /** The scope path where to save the response data to, or a function that returns it. */
  dataDst?: string | ((props: { resourceId: number }) => string);
  /** The data provided to the request. Either the data, or a function returning the data. */
  data?: (() => Data) | Data;
  /** Indicates that this resource is a "multipart/form-data", used to upload files. */
  multipart?: boolean;
  /** Indicates that this resource is a application/x-www-form-urlencoded". */
  formEncoded?: boolean;
  /** The scope where to read the request data from. Any value compatible with [mapScopeToProps](/Core/Utils/Redux%20utils/mapScopeToProps) is valid. */
  dataSrc?: MapScopedKeyMap<{ resourceId: number }>;
  /** The request method. */
  method?: Method;
  /** List of function that will be resolved before the request is executed. */
  requestResolvers?: ((requestConfig: AxiosRequestConfig) => Promise<AxiosRequestConfig> |
  Generator<TakeEffect, AxiosRequestConfig, any>)[];
  /** List of function that will be resolved before the response is returned. */
  responseResolvers?: ((response: AxiosResponse<Data>, options: { store: AugmentedStore<ResourceState>; scope: string[] }) => Promise<AxiosResponse<Data>>)[];
}

interface ResourceInternalsProps {
  resourceId: number;
  resourceReducerKey: string;
  scope: string[];
  error: boolean | Error;
  store: AugmentedStore<ResourceState>;
}

export interface ResourceComposedProps<Data, Error = {}> extends ResourceProps<Data, Error>, ResourceInternalsProps {}

export interface ResourceStatics {
  type: Symbol;
  defaultProps?: Partial<ResourceComposedProps<{}>>;
  publicProps: (StringKeyOf<ResourceProps<{}>>)[];
  scopeProps: string[];
}

const ResourceDefaultProps = {
  autoRequestIfEmpty: false,
};

const Resource: (<Data, Error = {}>(
  props: ResourceComposedProps<Data, Error>
) => ReactNode) & ResourceStatics = (props) => {
  const { registerLoader, unregisterLoader } = useContext(LoadBufferContext);
  const {
    children, name, resourceId, autoRequestIfEmpty, autoRequest, scope, requestOnLanguageChange, dataDst, dataSrc, params, url, method,
  } = props;
  const dispatch = useDispatch();
  const dispatchRequestResourceById = useCallback((...args: Parameters<typeof requestResourceById>) => dispatch(requestResourceById(...args)), [dispatch]);

  const { registerResource, unregisterResource } = useContext(ResourceGroupContext) || {};
  useEffect(() => {
    const currentDataDst = getDataDst({ dataDst, dataSrc });
    const asbDataDst = currentDataDst && `/${resolveScope(currentDataDst, scope)}`;

    if (registerResource) {
      registerResource({ resourceId, dataDst: asbDataDst });
      return () => {
        unregisterResource({ resourceId });
      };
    } if (registerLoader && autoRequest) {
      registerLoader({ resourceId, name });
      return () => {
        unregisterLoader({ resourceId, name });
      };
    }
    return undefined;
  }, [name, registerLoader, registerResource, resourceId, unregisterLoader, unregisterResource, autoRequest, dataDst, dataSrc, scope]);

  const { store } = useContext(ReactReduxContext);
  const { schema } = useContext(SchemaContext);
  const flattenedParams = useMemo(() => (params && JSON.stringify(params)) || '', [params]);
  useEffect(() => {
    if (autoRequestIfEmpty) {
      const path = schema.getDataDst({ resourceId, dataDst, dataSrc });
      const state = store.getState();
      const data = getScoped(state, path, scope);
      if (!data) {
        dispatchRequestResourceById(resourceId);
      }
    } else if (autoRequest) {
      dispatchRequestResourceById(resourceId);
    }
  }, [url, method, autoRequest, autoRequestIfEmpty, dataDst, dataSrc, dispatchRequestResourceById, name, resourceId, store, scope, flattenedParams, schema]);

  const reload = useCallback(() => dispatch(requestResourceById(resourceId)), [dispatch, resourceId]);
  const { language } = useTranslation();
  const initialRenderRef = useRef(true);
  useEffect(() => {
    if (!initialRenderRef.current && requestOnLanguageChange) {
      reload();
    } else {
      initialRenderRef.current = false;
    }
  }, [language, reload, requestOnLanguageChange]);

  const currentDataDst = getDataDst(props);
  const asbDataDst = currentDataDst && `/${resolveScope(currentDataDst, scope)}`;

  if (children) {
    return children({ name, resourceId, dataDst: asbDataDst });
  }

  return null;
};

Resource.type = Symbol('Resource');
Resource.defaultProps = ResourceDefaultProps;
Resource.publicProps = ['name', 'url', 'method', 'data', 'dataSrc', 'dataDst', 'multipart', 'formEncoded', 'config', 'transformRequest', 'transformResponse', 'headers', 'autoRequestIfEmpty', 'autoRequest', 'effect', 'onRequest', 'onSuccess', 'onFailure', 'onComplete', 'requestOnLanguageChange', 'params'];
Resource.scopeProps = ['resourceId', 'name', 'url', 'method', 'autoRequestIfEmpty', 'autoRequest', 'pending', 'error', 'attempts', 'dataDst', 'requestCount'];

/**
 * @visibleName Resource
 * @component
 */
export default Resource;
